import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { VideoPlayerModal } from '../components/VideoPlayerModal';
import {
  Play, Clock, Film, ChevronDown, ChevronRight, Search,
  Trophy, Users, X, Loader2, Filter, Sparkles
} from 'lucide-react';

const PAGE_SIZE = 30;
const LAST_VISIT_KEY = 'afrosports_highlights_last_visited';

function formatDuration(sec) {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `0:${String(s).padStart(2, '0')}`;
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

const TYPE_LABELS = {
  goals: 'Goals',
  highlight: 'Highlights',
  extended: 'Extended Highlights',
  replay: 'Full Match Replay',
  interview: 'Post Match Interview',
  analysis: 'Analysis',
  news: 'News',
};

const TYPE_ORDER = ['goals', 'highlight', 'extended', 'replay', 'interview', 'analysis', 'news'];

const Highlights = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalGroups, setTotalGroups] = useState(0);
  const [totalHighlights, setTotalHighlights] = useState(0);
  const [totalNew, setTotalNew] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({ competitions: [], teams: [], stages: [], seasons: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [selectedType, setSelectedType] = useState({});
  const [lastVisited, setLastVisited] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const sentinelRef = useRef(null);

  const competition = searchParams.get('competition') || '';
  const team = searchParams.get('team') || '';
  const search = searchParams.get('q') || '';

  // Load last visit timestamp on mount, update it after data loads
  useEffect(() => {
    const stored = localStorage.getItem(LAST_VISIT_KEY);
    if (stored) setLastVisited(stored);
  }, []);

  const fetchData = useCallback(async (newOffset = 0, append = false) => {
    if (newOffset === 0) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = new URLSearchParams({ offset: newOffset, limit: PAGE_SIZE });
      if (competition) params.set('competition', competition);
      if (team) params.set('team', team);

      const res = await fetch(`/api/content/highlights/gallery?${params}`);
      if (res.ok) {
        const data = await res.json();
        setGroups(prev => append ? [...prev, ...data.groups] : data.groups);
        setCompetitions(data.competitions || []);
        setHasMore(data.has_more);
        setTotalGroups(data.total_groups);
        setTotalHighlights(data.total_highlights);
        setTotalNew(data.total_new || 0);
        setOffset(newOffset + data.groups.length);
        setFilters(data.filters || { competitions: [], teams: [], stages: [], seasons: [] });

        // Save visit timestamp after first successful load
        if (newOffset === 0) {
          localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
        }
      }
    } catch (e) { console.error('Failed to fetch highlights:', e); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [competition, team]);

  useEffect(() => { fetchData(0); }, [fetchData]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
        fetchData(offset, true);
      }
    }, { rootMargin: '200px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, offset, fetchData]);

  const setFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    setSearchParams(newParams);
  };

  // Client-side search filter
  const filteredGroups = search
    ? groups.filter(g => {
        const q = search.toLowerCase();
        return (g.team_a || '').toLowerCase().includes(q)
          || (g.team_b || '').toLowerCase().includes(q)
          || (g.competition || '').toLowerCase().includes(q);
      })
    : groups;

  // Organize by competition
  const compMap = {};
  filteredGroups.forEach(g => {
    const comp = g.competition || 'Other';
    if (!compMap[comp]) compMap[comp] = { name: comp, season: g.season || '', matches: [] };
    compMap[comp].matches.push(g);
  });
  const organizedComps = Object.values(compMap);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="highlights-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 pt-24 pb-16">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
            Match Highlights
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-white/40 text-sm">
              {totalHighlights} videos across {totalGroups} matches
            </p>
            {totalNew > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-500/15 border border-emerald-500/20 rounded-full text-xs font-semibold text-emerald-400" data-testid="new-highlights-counter">
                <Sparkles className="w-3 h-3" />
                {totalNew} new this week
              </span>
            )}
          </div>
        </div>

        {/* Search + Filter bar */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              data-testid="highlight-search"
              value={search}
              onChange={e => setFilter('q', e.target.value)}
              placeholder="Search team or competition..."
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/50"
            />
            {search && (
              <button onClick={() => setFilter('q', '')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters"
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              showFilters || competition || team
                ? 'bg-emerald-600 text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {(competition || team) && (
              <span className="ml-1 w-5 h-5 bg-white/20 rounded-full text-xs flex items-center justify-center">
                {(competition ? 1 : 0) + (team ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* Filter chips */}
        {showFilters && (
          <div className="mb-6 space-y-3 bg-white/[0.02] rounded-xl p-4 border border-white/5" data-testid="filters-panel">
            {/* Competitions */}
            <div>
              <label className="text-xs text-white/40 mb-2 block">Competition</label>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setFilter('competition', '')}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${!competition ? 'bg-emerald-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}>
                  All
                </button>
                {filters.competitions.map(c => (
                  <button key={c} onClick={() => setFilter('competition', c)}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${competition === c ? 'bg-emerald-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            {/* Teams */}
            {filters.teams.length > 0 && (
              <div>
                <label className="text-xs text-white/40 mb-2 block">Team</label>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                  <button onClick={() => setFilter('team', '')}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${!team ? 'bg-emerald-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}>
                    All
                  </button>
                  {filters.teams.slice(0, 30).map(t => (
                    <button key={t} onClick={() => setFilter('team', t)}
                      className={`px-3 py-1 rounded-full text-xs transition-colors ${team === t ? 'bg-emerald-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Active filter tags */}
        {(competition || team) && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-xs text-white/30">Active:</span>
            {competition && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-600/20 text-emerald-400 rounded-full text-xs">
                <Trophy className="w-3 h-3" />{competition}
                <button onClick={() => setFilter('competition', '')} className="ml-1 hover:text-white"><X className="w-3 h-3" /></button>
              </span>
            )}
            {team && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-600/20 text-blue-400 rounded-full text-xs">
                <Users className="w-3 h-3" />{team}
                <button onClick={() => setFilter('team', '')} className="ml-1 hover:text-white"><X className="w-3 h-3" /></button>
              </span>
            )}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-20">
            <Film className="w-12 h-12 mx-auto text-white/10 mb-4" />
            <p className="text-white/40 text-lg">No highlights found</p>
            <p className="text-white/20 text-sm mt-1">Try a different search or filter</p>
          </div>
        ) : (
          <div className="space-y-10">
            {organizedComps.map(comp => (
              <CompetitionSection
                key={comp.name}
                comp={comp}
                expandedMatch={expandedMatch}
                setExpandedMatch={setExpandedMatch}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                lastVisited={lastVisited}
                onPlayVideo={setActiveVideo}
              />
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-4" />
        {loadingMore && (
          <div className="flex justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {activeVideo && (
        <VideoPlayerModal video={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </div>
  );
};

function CompetitionSection({ comp, expandedMatch, setExpandedMatch, selectedType, setSelectedType, lastVisited, onPlayVideo }) {
  const newMatchCount = comp.matches.filter(m => m.new_count > 0).length;
  return (
    <div data-testid={`comp-section-${comp.name}`}>
      {/* Competition header */}
      <div className="flex items-center gap-3 mb-4">
        <Trophy className="w-5 h-5 text-emerald-500" />
        <h2 className="text-lg font-bold text-white">{comp.name}</h2>
        {comp.season && <span className="text-xs text-white/30 bg-white/5 px-2 py-0.5 rounded">{comp.season}</span>}
        <span className="text-xs text-white/30">{comp.matches.length} match{comp.matches.length !== 1 ? 'es' : ''}</span>
        {newMatchCount > 0 && (
          <span className="text-xs text-emerald-400 font-medium" data-testid={`comp-new-count-${comp.name}`}>
            {newMatchCount} new
          </span>
        )}
      </div>

      {/* Matches grid */}
      <div className="space-y-3">
        {comp.matches.map(match => (
          <MatchCard
            key={match.group_key}
            match={match}
            isExpanded={expandedMatch === match.group_key}
            onToggle={() => setExpandedMatch(expandedMatch === match.group_key ? null : match.group_key)}
            selectedType={selectedType[match.group_key] || null}
            onSelectType={(t) => setSelectedType(prev => ({ ...prev, [match.group_key]: t === prev[match.group_key] ? null : t }))}
            lastVisited={lastVisited}
            onPlayVideo={onPlayVideo}
          />
        ))}
      </div>
    </div>
  );
}

function MatchCard({ match, isExpanded, onToggle, selectedType, onSelectType, lastVisited, onPlayVideo }) {
  const videoTypes = match.video_types || {};
  const sortedTypes = TYPE_ORDER.filter(t => videoTypes[t]);
  const hasNew = (match.new_count || 0) > 0;

  // Filter videos by selected type
  const displayVideos = selectedType
    ? match.videos.filter(v => v.video_type === selectedType)
    : match.videos;

  // Build metadata string: "PSL • Matchday 14 • 2024/25"
  const metaParts = [];
  if (match.competition) metaParts.push(match.competition);
  if (match.stage) metaParts.push(match.stage);
  if (match.season) metaParts.push(match.season);
  const metaStr = metaParts.join(' \u2022 ');

  return (
    <div className={`bg-white/[0.02] border rounded-xl overflow-hidden hover:scale-[1.01] hover:shadow-lg hover:shadow-emerald-900/10 transition-all duration-200 ${hasNew ? 'border-emerald-500/20 hover:border-emerald-500/30' : 'border-white/[0.06] hover:border-emerald-500/20'}`}
      data-testid={`match-card-${match.group_key}`}>
      {/* Match header — always visible */}
      <button onClick={onToggle}
        className="w-full flex items-center gap-0 text-left hover:bg-white/[0.02] transition-colors">
        {/* Thumbnail - enlarged to ~360x210 ratio */}
        <div className="w-[180px] h-[105px] sm:w-[220px] sm:h-[128px] bg-white/5 shrink-0 relative overflow-hidden">
          {match.thumbnail ? (
            <img src={match.thumbnail} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-900/30 to-[#0a0a0a]">
              <Film className="w-8 h-8 text-white/10" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a0a0a]/80" />
          {/* Video count badge */}
          <span className="absolute bottom-1.5 left-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
            {match.videos.length} clip{match.videos.length !== 1 ? 's' : ''}
          </span>
          {/* NEW badge on thumbnail */}
          {hasNew && (
            <span className="absolute top-1.5 right-1.5 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded tracking-wide animate-pulse" data-testid="new-badge">
              NEW
            </span>
          )}
        </div>

        {/* Match info */}
        <div className="flex-1 min-w-0 px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            {match.team_a && match.team_b ? (
              <span className="text-white font-bold text-sm">
                {match.team_a} <span className="text-white/30 font-normal">vs</span> {match.team_b}
              </span>
            ) : (
              <span className="text-white font-semibold text-sm truncate">{match.team_a || 'Unknown Match'}</span>
            )}
            {match.score && <span className="text-emerald-400 text-xs font-bold shrink-0 bg-emerald-500/10 px-1.5 py-0.5 rounded">{match.score}</span>}
            {hasNew && (
              <span className="text-emerald-400 text-[10px] font-semibold shrink-0" data-testid="new-clip-count">
                {match.new_count} new clip{match.new_count !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {/* Enhanced metadata line: "PSL • Matchday 14 • 2024/25" */}
          {metaStr && (
            <p className="text-white/40 text-xs mb-1.5 truncate" data-testid="match-metadata">
              {metaStr}
            </p>
          )}
          {/* Video type pills */}
          <div className="flex items-center gap-1 flex-wrap">
            {sortedTypes.map(t => (
              <span key={t} className="px-2 py-0.5 bg-white/5 rounded text-[10px] text-white/40">
                {TYPE_LABELS[t] || t} ({videoTypes[t]})
              </span>
            ))}
          </div>
        </div>

        {/* Expand chevron */}
        <div className="px-4 shrink-0">
          <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Expanded: Video type tabs + Videos */}
      {isExpanded && (
        <div className="border-t border-white/5">
          {/* Type filter tabs */}
          {sortedTypes.length > 1 && (
            <div className="flex items-center gap-1 px-4 py-2 border-b border-white/5 overflow-x-auto">
              <button
                onClick={() => onSelectType(null)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors shrink-0 ${
                  !selectedType ? 'bg-emerald-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                }`}
              >
                All ({match.videos.length})
              </button>
              {sortedTypes.map(t => (
                <button key={t} onClick={() => onSelectType(t)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors shrink-0 ${
                    selectedType === t ? 'bg-emerald-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                  }`}
                >
                  {TYPE_LABELS[t] || t} ({videoTypes[t]})
                </button>
              ))}
            </div>
          )}

          {/* Video list */}
          <div className="divide-y divide-white/5">
            {displayVideos.map(video => (
              <VideoRow key={video.id} video={video} lastVisited={lastVisited} onPlay={onPlayVideo} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VideoRow({ video, lastVisited, onPlay }) {
  const isNew = video.is_new || (lastVisited && video.published_at && video.published_at > lastVisited);

  const handleClick = () => {
    if (onPlay) onPlay(video);
  };

  return (
    <div
      onClick={handleClick}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.03] transition-colors group cursor-pointer"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') handleClick(); }}
      data-testid={`video-${video.id}`}
    >
      {/* Thumbnail */}
      <div className="w-24 h-14 rounded-lg overflow-hidden bg-white/5 shrink-0 relative">
        {video.thumbnail_url && (
          <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
          <Play className="w-6 h-6 text-white" />
        </div>
        {video.duration > 0 && (
          <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1 rounded">
            {formatDuration(video.duration)}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm text-white/80 group-hover:text-white line-clamp-1 transition-colors">
            {video.title}
          </p>
          {isNew && (
            <span className="shrink-0 bg-emerald-500/20 text-emerald-400 text-[9px] font-bold px-1.5 py-0.5 rounded tracking-wide" data-testid="video-new-badge">
              NEW
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-[10px] text-white/20 bg-white/5 px-1.5 py-0.5 rounded capitalize">
            {TYPE_LABELS[video.video_type] || video.video_type}
          </span>
          {video.published_at && (
            <span className="text-[10px] text-white/20">{formatTimeAgo(video.published_at)}</span>
          )}
          {video.views > 0 && (
            <span className="text-[10px] text-white/20">{(video.views / 1000).toFixed(0)}K views</span>
          )}
        </div>
      </div>

      <Play className="w-4 h-4 text-white/10 group-hover:text-emerald-400 shrink-0 transition-colors" />
    </div>
  );
}

export default Highlights;
